import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { useChatSearchStore } from '../../store/chatSearchStore';
import useChatSearch from '../../hooks/useChatSearch';
import { autocompleteContext, applySuggestion } from '../../utils/chatSearchQuery';
import { buildSuggestions } from '../../utils/chatSearchSuggestions';
import { parseDay } from '../../utils/chatSearchParams';
import ChatSearchAutocomplete from './ChatSearchAutocomplete';
import './ChatSearch.css';

// Outside click closes the menu. The DatePicker portals its calendar (and a
// backdrop) outside our menu, so clicks there count as inside.
function useCloseOnOutside(open, refs, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const tgt = e.target;
      if (refs.some((r) => r.current?.contains(tgt))) return;
      if (tgt.closest?.('.date-picker-backdrop, .date-picker-menu')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * The conversation header's search field (Discord-style). Plain text plus
 * filter tokens; typing a key opens the matching suggestions. Enter runs the
 * search (D3) — unless a suggestion is being picked. Esc closes the menu
 * first, then clears the search and closes the results.
 */
export default function ChatSearchBar({ channel }) {
  const { t } = useTranslation();
  const channelId = channel?.id ?? null;
  const input = useChatSearchStore((s) => (s.channelId === channelId ? s.input : ''));
  const { run, cancel } = useChatSearch(channel);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const pendingCaretRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const listId = useId();

  const ctx = useMemo(() => autocompleteContext(input, caret), [input, caret]);
  const items = useMemo(() => buildSuggestions(ctx, channel?.members), [ctx, channel?.members]);
  const showMenu = menuOpen && items.length > 0;

  useEffect(() => setActive(0), [ctx?.mode, ctx?.partial]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (el && pendingCaretRef.current != null) {
      el.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current);
      setCaret(pendingCaretRef.current);
      pendingCaretRef.current = null;
    }
  }, [input]);

  useCloseOnOutside(showMenu, [wrapRef, menuRef], () => setMenuOpen(false));

  const setInput = useCallback(
    (value) => {
      const s = useChatSearchStore.getState();
      if (s.channelId !== channelId) s.reset(channelId);
      useChatSearchStore.getState().setInput(value);
    },
    [channelId]
  );

  const syncCaret = () => setCaret(inputRef.current?.selectionStart ?? 0);

  const insertToken = (token) => {
    if (!ctx) return;
    const next = applySuggestion(input, ctx.range, token);
    pendingCaretRef.current = next.caret;
    setInput(next.str);
    setMenuOpen(true);
    inputRef.current?.focus();
  };

  const pick = (item) => item?.token && insertToken(item.token);

  const submit = () => {
    setMenuOpen(false);
    run(input);
  };

  const clear = () => {
    cancel();
    setInput('');
    useChatSearchStore.getState().close();
  };

  const onKeyDown = (e) => {
    const pickable = items.filter((i) => i.token);
    if (e.key === 'ArrowDown' && showMenu) {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, pickable.length - 1));
    } else if (e.key === 'ArrowUp' && showMenu) {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Tab' && showMenu && pickable[active]) {
      e.preventDefault();
      pick(pickable[active]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = pickable[active];
      const typed = ctx ? input.slice(ctx.range[0], ctx.range[1]) : '';
      // Pick a suggestion only while one is actually being typed; a finished
      // token (or plain text no key starts with) runs the search. A date the
      // user typed in full is finished too: the date menu stays open under it,
      // and Enter used to replace `before:2026-08-02` with its first row, Today.
      const finishedDate = ctx?.mode === 'date' && parseDay(ctx.partial) != null;
      if (showMenu && item && !finishedDate && !(ctx.mode === 'keys' && !ctx.partial) && item.token !== typed) pick(item);
      else submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (showMenu) setMenuOpen(false);
      else clear();
    }
  };

  return (
    <div ref={wrapRef} className={`chat-search-bar${focused || input ? ' chat-search-bar--expanded' : ''}`}>
      <input
        ref={inputRef}
        type="text"
        className="chat-search-bar-input"
        placeholder={t('chat.search.placeholder')}
        aria-label={t('chat.search.placeholder')}
        role="combobox"
        aria-expanded={showMenu}
        aria-controls={listId}
        aria-autocomplete="list"
        spellCheck={false}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          setMenuOpen(true);
        }}
        onKeyDown={onKeyDown}
        onKeyUp={syncCaret}
        onClick={() => {
          syncCaret();
          setMenuOpen(true);
        }}
        onFocus={() => {
          setFocused(true);
          setMenuOpen(true);
          syncCaret();
        }}
        onBlur={() => setFocused(false)}
      />
      {input ? (
        <Tooltip content={t('chat.search.clear')} side="bottom">
          <button type="button" className="chat-search-bar-icon chat-search-bar-clear" onClick={clear}>
            <X size={14} />
          </button>
        </Tooltip>
      ) : (
        <span className="chat-search-bar-icon" aria-hidden>
          <Search size={14} />
        </span>
      )}
      {showMenu && (
        <ChatSearchAutocomplete
          anchorRef={wrapRef}
          menuRef={menuRef}
          listId={listId}
          ctx={ctx}
          items={items}
          activeIndex={items.indexOf(items.filter((i) => i.token)[active])}
          onHover={(i) => setActive(items.filter((x) => x.token).indexOf(items[i]))}
          onPick={pick}
          onPickDate={(day) => insertToken(`${ctx.key}:${day}`)}
        />
      )}
    </div>
  );
}
